/**
 * Menu status label rules (handoff "Menu status labels"): a label shows only when
 * someone must act; nothing when fine. Red is safety only, amber needs attention,
 * grey is a count to do; a bare count caps at 9+ and a count with its word is exact. Worklist and Atlas also show a
 * green "Clear" / "Live" word, like Guardian.
 *
 * The server computes the label; this decides whether and how to draw it, and
 * refuses to draw red on an item that is not a safety item.
 */
import type { NavBadge } from './api/types';
import type { BadgeKey } from './roles';
import type { ToneName } from './tokens/tokens';

/** The only menu items allowed to go red (safety): Guardian, System Pulse, Oversight paused, Atlas offline. */
export const RED_ALLOWED: ReadonlySet<BadgeKey> = new Set<BadgeKey>(['guardian', 'pulse', 'oversight', 'companion']);

export function capCount(n: number): string {
  return n > 9 ? '9+' : String(n);
}

export interface BadgeView {
  text: string;
  tone: ToneName;
  aria: string;
  modeled: boolean;
}

/**
 * What to draw for one key, or null for nothing. A null badge (the source could not
 * answer) draws nothing rather than a made-up zero.
 */
export function badgeView(key: BadgeKey, badge: NavBadge | null | undefined): BadgeView | null {
  if (!badge || badge.tone === 'none') return null;
  let text = badge.label;
  if (text === null || text === '') {
    if (badge.n === null || badge.n <= 0) return null;
    text = capCount(badge.n);
  }
  // A bare number from the server still obeys the 9+ cap.
  if (/^\d+$/.test(text)) text = capCount(Number(text));
  const tone: ToneName = badge.tone === 'red' && !RED_ALLOWED.has(key) ? 'amber' : badge.tone;
  return { text, tone, aria: badge.aria, modeled: badge.modeled === true };
}
